import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Box,
  Input,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import { isBlank, notBlank } from "../util/ext";
import Grid from "@mui/material/Grid";
import {
  map,
  find,
  lensProp,
  filter,
  propEq,
  set,
  pluck,
  union,
  reduce,
  difference,
  mergeRight,
  mergeLeft,
  lift,
  partial,
  flip,
  assoc,
  pick,
} from "ramda";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import SubscriptionTypeSelect from "./SubscriptionTypeSelect";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Skeleton } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useModuleGroups } from "../manage-module-groups/moduleGroupsService";
import {
  useSubscriptionCreate,
  useSubscriptionUpdate,
} from "./subscriptionService";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import { matchSorter } from "match-sorter";
import VirtualizedListbox, {
  LISTBOX_BORDER_STYLE,
} from "../components/VirtualizedListbox";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";

const useStyles = makeStyles(theme => ({
  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  selectEmpty: {
    marginTop: theme.spacing(2),
  },

  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const moduleFilterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: ["name", "displayName", "id", "status"],
  });

const PERPETUAL_END_DATE = "4000-01-01T00:00:00Z";

const initialFields = {
  type: "REGULAR",
  startDate: null,
  endDate: null,
  salesPrice: 0.0,
  perpetual: false,
};

const SubscriptionDialog = ({
  open,
  organizationId,
  subscription,
  moduleList,
  preselectedModules = [],
  organizationList,
  onSave,
  onClose,
  useAllModules = false,
}) => {
  const classes = useStyles();
  const [errors, setErrors] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedModules, setSelectedModules] = useState(preselectedModules);
  const [selectedModuleGroups, setSelectedModuleGroups] = useState([]);
  const [fields, setFields] = useState(initialFields);

  const moduleGroupFetch = useModuleGroups();
  const [moduleGroups, setModuleGroups] = useState([]);

  const [dialogWidth, setDialogWidth] = useState("md");
  const [fullScreen, setFullScreen] = useState(false);

  const findOrganization = useCallback(
    orgId => {
      return find(propEq("organizationId", orgId), organizationList);
    },
    [organizationList],
  );

  const findModule = useCallback(
    moduleId => {
      return find(propEq("id", moduleId), moduleList);
    },
    [moduleList],
  );

  useEffect(() => {
    if (open && notBlank(organizationList) && notBlank(moduleList)) {
      setOrganizations(filter(org => org.active, organizationList));
      const modules = useAllModules
        ? moduleList
        : filter(propEq("status", "ACTIVE"), moduleList);

      setModules(modules);

      if (notBlank(subscription)) {
        setFields(
          pick(
            ["type", "startDate", "endDate", "salesPrice", "perpetual"],
            subscription,
          ),
        );

        setSelectedOrganizations([subscription.organizationId]);

        const module = findModule(subscription.mediaId);
        setSelectedModules([module]);
      } else {
        setFields(initialFields);
        if (notBlank(organizationId)) {
          setSelectedOrganizations([organizationId]);
        } else {
          setSelectedOrganizations([]);
        }
      }
    }
  }, [
    open,
    organizationList,
    moduleList,
    organizationId,
    findOrganization,
    findModule,
    subscription,
    useAllModules,
  ]);

  useEffect(() => {
    if (open && moduleGroupFetch.isSuccess && notBlank(moduleGroupFetch.data)) {
      setModuleGroups(moduleGroupFetch.data);
    }
  }, [open, moduleGroupFetch.isSuccess, moduleGroupFetch.data]);

  const onFieldChanged = field => value => {
    const val = value && value.target ? value.target.value : value;
    const update = set(lensProp(field), val, fields);
    setFields(update);
  };

  const onPerpetualChanged = useCallback(
    event => {
      const perpetual = event.target.checked;
      const definiteEnd = notBlank(subscription) ? subscription.endDate : null;
      const endDate = perpetual ? PERPETUAL_END_DATE : definiteEnd;

      setFields(mergeLeft({ perpetual, endDate }));
    },
    [subscription],
  );

  const handleOrganizationsChange = useCallback(ids => {
    setSelectedOrganizations(ids);
  }, []);

  const handleModulesChange = useCallback((_event, modules) => {
    setSelectedModules(modules);
  }, []);

  useEffect(() => {
    if (selectedModules.length > 100) {
      setFullScreen(true);
    } else {
      setFullScreen(false);
      if (selectedModules.length > 80) {
        setDialogWidth("xl");
      } else if (selectedModules.length > 60) {
        setDialogWidth("lg");
      } else if (selectedModules.length > 30) {
        setDialogWidth("md");
      } else {
        setDialogWidth("sm");
      }
    }
  }, [selectedModules]);

  const handleModuleGroupsChange = useCallback(
    (_event, moduleGroups) => {
      setSelectedModuleGroups(moduleGroups);

      const diff = difference(selectedModuleGroups, moduleGroups);
      if (notBlank(diff)) {
        const modules = map(
          id => find(propEq("id", id), moduleList),
          pluck("moduleId", reduce(union, [], pluck("modules", diff))),
        );

        setSelectedModules(prev => difference(prev, modules));
      } else {
        const modules = map(
          id => find(propEq("id", id), moduleList),
          pluck("moduleId", reduce(union, [], pluck("modules", moduleGroups))),
        );
        setSelectedModules(prev => union(prev, modules));
      }
    },
    [selectedModuleGroups, moduleList],
  );

  const validate = useCallback(() => {
    const errorList = [];
    if (isBlank(selectedOrganizations)) {
      errorList.push("At least one Organization must be selected.");
    }
    if (isBlank(selectedModules)) {
      errorList.push("At least one Module must be selected.");
    }
    if (isBlank(fields.startDate)) {
      errorList.push("Start Date is required.");
    }
    if (isBlank(fields.endDate)) {
      errorList.push("End Date is required.");
    }

    return errorList;
  }, [selectedOrganizations, selectedModules, fields]);

  const createSubscription = useSubscriptionCreate();
  const updateSubscription = useSubscriptionUpdate();
  const handleApply = useCallback(async () => {
    const errorList = validate();
    if (isBlank(errorList)) {
      const xProdObj = lift(mergeRight);
      const zipMediaId = lift(partial(flip(partial(assoc, ["mediaId"])), [{}]));

      try {
        const subs = map(
          mergeRight(fields),
          xProdObj(
            selectedOrganizations.map(id => ({ organizationId: id })),
            zipMediaId(pluck(["id"], selectedModules)),
          ),
        );

        if (isBlank(subscription)) {
          await Promise.all(
            map(sub => createSubscription.mutateAsync({ data: sub }), subs),
          );
        } else {
          await Promise.all(
            map(
              sub =>
                updateSubscription.mutateAsync({
                  resourceIds: { id: subscription.id },
                  data: sub,
                }),
              subs,
            ),
          );
        }

        onSave(subs);
      } catch (err) {
        if (notBlank(err.body)) {
          setErrors(pluck("message", err.body.errors));
        } else {
          setErrors([err.message]);
        }
      }
    } else {
      setErrors(errorList);
    }
  }, [
    validate,
    selectedModules,
    selectedOrganizations,
    fields,
    subscription,
    createSubscription,
    updateSubscription,
    onSave,
  ]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth={dialogWidth}
      open={open}
      aria-labelledby="subscription-dialog-title"
    >
      <DialogTitle id="subscription-dialog-title">Add Subscription</DialogTitle>
      <DialogContent>
        {notBlank(errors) && (
          <div className={classes.errorContainer}>
            {errors.map((err, idx) => (
              <div key={idx}>{err}</div>
            ))}
          </div>
        )}
        <Box>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Grid alignItems="flex-end" container spacing={5}>
              <Grid item sm={12}>
                <Box>
                  <Typography variant="h5">
                    Select one or more Organizations.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {notBlank(organizations) ? (
                  <Box className={classes.multiPicker}>
                    <OrganizationAutoComplete
                      multiple
                      label="Organizations..."
                      TextFieldProps={{ variant: "outlined" }}
                      value={selectedOrganizations || []}
                      onChange={handleOrganizationsChange}
                      disabled={
                        notBlank(subscription) &&
                        notBlank(subscription.organizationId)
                      }
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                <Box mt={2}>
                  <Typography variant="h5">
                    Select one or more Modules.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {notBlank(moduleGroups) ? (
                  <Box className={classes.multiPicker}>
                    <Autocomplete
                      multiple
                      id="moduleGroups"
                      options={moduleGroups}
                      getOptionLabel={option =>
                        notBlank(option) ? option.moduleGroupName : ""
                      }
                      value={selectedModuleGroups || []}
                      filterSelectedOptions
                      onChange={handleModuleGroupsChange}
                      disabled={
                        notBlank(subscription) && notBlank(subscription.mediaId)
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Module Groups"
                          placeholder="Select..."
                        />
                      )}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                {notBlank(modules) ? (
                  <Box className={classes.multiPicker}>
                    <Autocomplete
                      multiple
                      id="modules"
                      options={modules}
                      ListboxComponent={VirtualizedListbox}
                      ListboxProps={{
                        itemSize: 60,
                        listStyle: LISTBOX_BORDER_STYLE,
                      }}
                      getOptionLabel={option =>
                        notBlank(option) ? option.name : ""
                      }
                      value={selectedModules || []}
                      filterSelectedOptions
                      filterOptions={moduleFilterOptions}
                      onChange={handleModulesChange}
                      disabled={
                        notBlank(subscription) && notBlank(subscription.mediaId)
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box width="100%">
                            <Box fontSize="1.4rem">
                              <Typography
                                noWrap
                                color={
                                  option.status === "INACTIVE"
                                    ? "error"
                                    : "inherit"
                                }
                              >
                                {option.name}
                              </Typography>
                            </Box>
                            <Box fontSize="1.2rem">
                              <Typography
                                color={
                                  option.status === "INACTIVE"
                                    ? "error"
                                    : "inherit"
                                }
                              >{`(${option.id})`}</Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Modules"
                          placeholder="Select..."
                        />
                      )}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                <Box mt={2}>
                  <Typography variant="h5">
                    Enter remaining subscription details.
                  </Typography>
                </Box>
              </Grid>
              <Grid item sm={6}>
                <DatePicker
                  format="MM/dd/yyyy"
                  label="Start Date"
                  value={
                    fields?.startDate
                      ? DateTime.fromISO(fields.startDate)
                      : null
                  }
                  onChange={onFieldChanged("startDate")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: "startDate",
                    },
                    openPickerButton: {
                      "aria-label": "change start date",
                    },
                  }}
                />
              </Grid>

              <Grid item sm={6}>
                {!fields.perpetual && (
                  <DatePicker
                    format="MM/dd/yyyy"
                    label="End Date"
                    maxDate={DateTime.fromISO("6000-01-01")}
                    value={
                      fields?.endDate ? DateTime.fromISO(fields.endDate) : null
                    }
                    onChange={onFieldChanged("endDate")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "endDate",
                      },
                      openPickerButton: {
                        "aria-label": "change end date",
                      },
                    }}
                  />
                )}
              </Grid>

              <Grid item sm={6}>
                <SubscriptionTypeSelect
                  value={fields.type}
                  label="Subscription Type"
                  required
                  onChange={onFieldChanged("type")}
                />
              </Grid>

              <Grid item sm={6}>
                <FormControl fullWidth className={classes.margin}>
                  <InputLabel htmlFor="salesPrice">Sales Price</InputLabel>
                  <Input
                    id="salesPrice"
                    value={fields.salesPrice}
                    onChange={onFieldChanged("salesPrice")}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item sm={12}>
                <FormControl fullWidth={true} className={classes.formControl}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fields.perpetual}
                        onChange={onPerpetualChanged}
                        name="perpetual"
                        color="primary"
                      />
                    }
                    label="Perpetual"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDialog;
